/**
 * Book Processor Utility
 * Processes raw book files (txt/epub) to extract clean reading text and chapter navigation
 */

export interface ProcessedChapter {
  id: string;
  number: number;
  title: string;
  startIndex: number;  // Character position in clean text
  endIndex: number;
  wordCount: number;
}

export interface ProcessedBook {
  title: string;
  author: string;
  cleanText: string;  // Pure reading text without chapter headers
  chapters: ProcessedChapter[];  // For navigation only
  totalWords: number;
  totalChapters: number;
  language: string;
  source: string;  // Original filename
}

export interface ProcessingResult {
  success: boolean;
  book?: ProcessedBook;
  error?: string;
  warnings?: string[];
}

// Common patterns for chapter detection
const CHAPTER_PATTERNS = [
  /^CHAPTER\s+(\d+|[IVXLCDM]+)\.?\s*(.*)$/im,
  /^Chapter\s+(\d+|[IVXLCDM]+)\.?\s*(.*)$/im,
  /^(\d+)\.\s+(.+)$/im,  // "1. Title" format
  /^Part\s+(\d+|[IVXLCDM]+)\.?\s*(.*)$/im,
];

// Patterns for content to remove (headers, footers, etc.)
const REMOVE_PATTERNS = [
  /\*\*\* START OF THE PROJECT GUTENBERG EBOOK.*?\*\*\*/is,
  /\*\*\* END OF THE PROJECT GUTENBERG EBOOK.*?\*\*\*/is,
  /Produced by.*?\n\n/is,
  /The Project Gutenberg eBook.*?\n\n/is,
  /\[Illustration.*?\]/gi,
  /\[.*?illustration.*?\]/gi,
];

// Roman numeral conversion
const ROMAN_NUMERALS: Record<string, number> = {
  'I': 1, 'II': 2, 'III': 3, 'IV': 4, 'V': 5,
  'VI': 6, 'VII': 7, 'VIII': 8, 'IX': 9, 'X': 10,
  'XI': 11, 'XII': 12, 'XIII': 13, 'XIV': 14, 'XV': 15,
  'XVI': 16, 'XVII': 17, 'XVIII': 18, 'XIX': 19, 'XX': 20,
  'XXI': 21, 'XXII': 22, 'XXIII': 23, 'XXIV': 24, 'XXV': 25,
  'XXVI': 26, 'XXVII': 27, 'XXVIII': 28, 'XXIX': 29, 'XXX': 30,
  'XXXI': 31, 'XXXII': 32, 'XXXIII': 33, 'XXXIV': 34, 'XXXV': 35,
  'XXXVI': 36, 'XXXVII': 37, 'XXXVIII': 38, 'XXXIX': 39, 'XL': 40,
  'XLI': 41, 'XLII': 42, 'XLIII': 43, 'XLIV': 44, 'XLV': 45,
  'XLVI': 46, 'XLVII': 47, 'XLVIII': 48, 'XLIX': 49, 'L': 50,
  'LI': 51, 'LII': 52, 'LIII': 53, 'LIV': 54, 'LV': 55,
  'LVI': 56, 'LVII': 57, 'LVIII': 58, 'LIX': 59, 'LX': 60,
  'LXI': 61, 'LXII': 62, 'LXIII': 63, 'LXIV': 64, 'LXV': 65,
  'LXVI': 66, 'LXVII': 67, 'LXVIII': 68, 'LXIX': 69, 'LXX': 70,
  'LXXI': 71, 'LXXII': 72, 'LXXIII': 73, 'LXXIV': 74, 'LXXV': 75,
  'LXXVI': 76, 'LXXVII': 77, 'LXXVIII': 78, 'LXXIX': 79, 'LXXX': 80,
  'LXXXI': 81, 'LXXXII': 82, 'LXXXIII': 83, 'LXXXIV': 84, 'LXXXV': 85,
  'LXXXVI': 86, 'LXXXVII': 87, 'LXXXVIII': 88, 'LXXXIX': 89, 'XC': 90,
  'XCI': 91, 'XCII': 92, 'XCIII': 93, 'XCIV': 94, 'XCV': 95,
  'XCVI': 96, 'XCVII': 97, 'XCVIII': 98, 'XCIX': 99, 'C': 100
};

function romanToArabic(roman: string): number | null {
  return ROMAN_NUMERALS[roman.toUpperCase()] || null;
}

/**
 * Extract metadata from book content
 */
function extractMetadata(content: string, filename: string): { title: string; author: string; language: string } {
  let title = filename.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
  let author = 'Unknown Author';
  let language = 'en';

  // Try Project Gutenberg format
  const titleMatch = content.match(/Title:\s*(.+)/i);
  if (titleMatch) {
    title = titleMatch[1].trim();
  }

  const authorMatch = content.match(/Author:\s*(.+)/i);
  if (authorMatch) {
    author = authorMatch[1].trim();
  }

  const languageMatch = content.match(/Language:\s*(.+)/i);
  if (languageMatch) {
    language = languageMatch[1].trim().toLowerCase().substring(0, 2);
  }

  return { title, author, language };
}

/**
 * Find content boundaries (skip front matter)
 */
function findContentBoundaries(content: string): { start: number; end: number } {
  let start = 0;
  let end = content.length;

  // Find start marker
  const startMarker = content.indexOf('*** START OF THE PROJECT GUTENBERG EBOOK');
  if (startMarker > -1) {
    const afterMarker = content.indexOf('\n', startMarker);
    if (afterMarker > -1) {
      start = afterMarker + 1;
    }
  }

  // Find end marker
  const endMarker = content.indexOf('*** END OF THE PROJECT GUTENBERG EBOOK');
  if (endMarker > -1) {
    end = endMarker;
  }

  return { start, end };
}

/**
 * Detect chapters in the content
 */
function detectChapters(content: string, startIndex: number): { number: number; title: string; position: number }[] {
  const chapters: { number: number; title: string; position: number }[] = [];
  
  // Try each pattern
  for (const pattern of CHAPTER_PATTERNS) {
    const regex = new RegExp(pattern.source, pattern.flags || 'gm');
    let match;
    let foundAny = false;

    while ((match = regex.exec(content)) !== null) {
      // Only consider chapters after the front matter
      if (match.index < startIndex) continue;
      
      foundAny = true;
      const chapterNum = romanToArabic(match[1]) || parseInt(match[1]) || chapters.length + 1;
      const chapterTitle = match[2] ? match[2].trim() : `Chapter ${chapterNum}`;
      
      chapters.push({
        number: chapterNum,
        title: chapterTitle,
        position: match.index
      });
    }

    if (foundAny && chapters.length > 0) {
      break; // Use first successful pattern
    }
  }

  // Sort by position and remove duplicates
  chapters.sort((a, b) => a.position - b.position);
  
  // Remove duplicates (same position)
  const uniqueChapters = chapters.filter((ch, i) => 
    i === 0 || ch.position !== chapters[i - 1].position
  );

  return uniqueChapters;
}

/**
 * Clean text by removing headers, footers, and formatting artifacts
 */
function cleanTextContent(content: string): string {
  let cleaned = content;

  // Remove known patterns
  for (const pattern of REMOVE_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }

  // Normalize whitespace
  cleaned = cleaned.replace(/\r\n/g, '\n');  // Normalize line endings
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');  // Max 2 consecutive newlines
  cleaned = cleaned.replace(/[ \t]+/g, ' ');  // Normalize spaces
  cleaned = cleaned.replace(/^ +/gm, '');  // Remove leading spaces on lines
  cleaned = cleaned.replace(/ +$/gm, '');  // Remove trailing spaces on lines

  return cleaned.trim();
}

/**
 * Remove chapter headers from text while preserving content
 */
function removeChapterHeaders(text: string, chapters: { number: number; title: string; position: number }[]): string {
  if (chapters.length === 0) return text;

  // Build a new string without chapter headers
  const lines = text.split('\n');
  const resultLines: string[] = [];
  
  let currentChapterIndex = 0;
  let skipNextLine = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Check if this line matches a chapter header
    let isChapterHeader = false;
    for (const pattern of CHAPTER_PATTERNS) {
      // Create fresh regex to avoid state consumption from lastIndex
      const regex = new RegExp(pattern.source, pattern.flags || 'im');
      if (regex.test(line)) {
        isChapterHeader = true;
        break;
      }
    }

    if (isChapterHeader) {
      // Skip this line (chapter header)
      skipNextLine = false;
      continue;
    }

    // Skip empty lines immediately after chapter header
    if (skipNextLine && line === '') {
      skipNextLine = false;
      continue;
    }

    resultLines.push(lines[i]);
  }

  return resultLines.join('\n');
}

/**
 * Process a book file and extract clean text with chapter navigation
 */
export function processBook(content: string, filename: string): ProcessingResult {
  const warnings: string[] = [];

  try {
    // Extract metadata
    const { title, author, language } = extractMetadata(content, filename);

    // Find content boundaries
    const { start, end } = findContentBoundaries(content);
    const mainContent = content.substring(start, end);

    // Detect chapters
    const detectedChapters = detectChapters(content, start);

    if (detectedChapters.length === 0) {
      warnings.push('No chapters detected - treating entire content as one section');
    }

    // Clean the text
    let cleanText = cleanTextContent(mainContent);

    // Remove chapter headers from reading text
    cleanText = removeChapterHeaders(cleanText, detectedChapters);

    // Calculate chapter positions in clean text
    const chapters: ProcessedChapter[] = [];
    let totalWords = 0;

    if (detectedChapters.length > 0) {
      for (let i = 0; i < detectedChapters.length; i++) {
        const ch = detectedChapters[i];
        const nextCh = detectedChapters[i + 1];
        
        // Find chapter content in original text
        const chapterStart = ch.position - start;
        const chapterEnd = nextCh ? nextCh.position - start : mainContent.length;
        const chapterContent = mainContent.substring(chapterStart, chapterEnd);
        
        // Clean chapter content
        const cleanChapterContent = cleanTextContent(chapterContent);
        const wordCount = cleanChapterContent.split(/\s+/).filter(w => w.length > 0).length;

        // Find position in clean text (approximate)
        const cleanChapterText = removeChapterHeaders(cleanChapterContent, [{ ...ch, position: 0 }]);
        const positionInCleanText = cleanText.indexOf(cleanChapterText.substring(0, 50));

        chapters.push({
          id: String(ch.number),
          number: ch.number,
          title: ch.title,
          startIndex: positionInCleanText >= 0 ? positionInCleanText : totalWords,
          endIndex: positionInCleanText >= 0 ? positionInCleanText + cleanChapterText.length : 0,
          wordCount
        });

        totalWords += wordCount;
      }
    } else {
      // Single chapter for entire book
      totalWords = cleanText.split(/\s+/).filter(w => w.length > 0).length;
      chapters.push({
        id: '1',
        number: 1,
        title: 'Full Book',
        startIndex: 0,
        endIndex: cleanText.length,
        wordCount: totalWords
      });
    }

    const book: ProcessedBook = {
      title,
      author,
      cleanText,
      chapters,
      totalWords,
      totalChapters: chapters.length,
      language,
      source: filename
    };

    return {
      success: true,
      book,
      warnings: warnings.length > 0 ? warnings : undefined
    };

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error processing book'
    };
  }
}

/**
 * Get chapter content from processed book
 */
export function getChapterContent(book: ProcessedBook, chapterId: string): string {
  const chapter = book.chapters.find(ch => ch.id === chapterId);
  if (!chapter) return '';
  
  return book.cleanText.substring(chapter.startIndex, chapter.endIndex);
}

/**
 * Get all chapter titles for navigation
 */
export function getChapterNavigation(book: ProcessedBook): { id: string; title: string; wordCount: number }[] {
  return book.chapters.map(ch => ({
    id: ch.id,
    title: ch.title,
    wordCount: ch.wordCount
  }));
}

/**
 * Calculate reading time estimate
 */
export function estimateReadingTime(wordCount: number, wpm: number = 250): number {
  return Math.ceil(wordCount / wpm);
}
